import React from 'react';
import { BoardIcon } from '@trello/nachos/icons/board';
import { PowerUpIcon } from '@trello/nachos/icons/power-up';
import { ButlerBotIcon } from '@trello/nachos/icons/butler-bot';
import { SubscribeIcon } from '@trello/nachos/icons/subscribe';
import { HeartIcon } from '@trello/nachos/icons/heart';
import { StarIcon } from '@trello/nachos/icons/star';
import { BusinessClassIcon } from '@trello/nachos/icons/business-class';
import { Tooltip, TooltipPrimitiveLight } from '@trello/nachos/tooltip';
import classNames from 'classnames';
import styles from './PlanSelection.less';
import { forNamespace, asMoney } from 'app/common/lib/util/i18n';
import { OrganizationFeatures } from '@trello/product-features';
import { UpgradePromptTestIds } from '@trello/test-ids';

const format = forNamespace('upgrade prompt plan selection');
const getPlanFeatureStrings = (plan: string, feature: string) => {
  const namespace = [plan, 'features', feature];

  return {
    title: plan === 'free team' ? null : format([...namespace, 'title']),
    content: format([...namespace, 'content']),
    tooltip:
      plan === 'free team' || feature === 'boards'
        ? null
        : format([...namespace, 'tooltip']),
  };
};

// exported for testing
export const bcPlanSelectionList = [
  {
    icon: <BoardIcon />,
    ...getPlanFeatureStrings('bc team', 'boards'),
  },
  {
    icon: <PowerUpIcon />,
    ...getPlanFeatureStrings('bc team', 'pups'),
  },
  {
    icon: <ButlerBotIcon />,
    ...getPlanFeatureStrings('bc team', 'butler'),
  },
  {
    icon: <SubscribeIcon />,
    ...getPlanFeatureStrings('bc team', 'admin'),
  },
  {
    icon: <HeartIcon />,
    ...getPlanFeatureStrings('bc team', 'support'),
  },
  {
    icon: <StarIcon />,
    ...getPlanFeatureStrings('bc team', 'more'),
  },
];

const freeTeamSelectionList = [
  {
    icon: <BoardIcon />,
    ...getPlanFeatureStrings('free team', 'boards'),
  },
  {
    icon: <PowerUpIcon />,
    ...getPlanFeatureStrings('free team', 'pups'),
  },
  {
    icon: <ButlerBotIcon />,
    ...getPlanFeatureStrings('free team', 'butler'),
  },
];

const renderTooltip = (tooltip: string | null, target: React.ReactNode) =>
  tooltip ? (
    <Tooltip content={tooltip} component={TooltipPrimitiveLight}>
      {target}
    </Tooltip>
  ) : (
    target
  );

interface PlanSelectionProps {
  cta: React.ReactNode;
}

const pricePerUser = format('price per user', {
  price: `$${asMoney(
    OrganizationFeatures.getPrice(
      OrganizationFeatures.currentProducts.yearly,
    )! / 12,
  )}`,
});

export const PlanSelection: React.FunctionComponent<PlanSelectionProps> = ({
  cta,
}) => (
  <>
    <div
      className={styles.heading}
      data-test-id={UpgradePromptTestIds.PlanSelection}
    >
      <h1>
        <BusinessClassIcon
          color="dark"
          dangerous_className={styles.headingIcon}
        />{' '}
        {format('headline')}
      </h1>
      <h2>{format('description')}</h2>
    </div>

    <div className={styles.bodyWrapper}>
      <div className={styles.freePlan}>
        <h2>{format(['free team', 'headline'])}</h2>
        <ul>
          {freeTeamSelectionList.map(({ icon, content }, index) => (
            <li key={index} className={styles.featureItem}>
              {React.cloneElement(icon, {
                size: 'medium',
                dangerous_className: styles.featureIcon,
              })}
              <span className={styles.featureContent}>{content}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.bcPlan}>
        <div className={styles.recommendedLabel}>Recommended</div>
        <h2>{format(['bc team', 'headline'])}</h2>
        <ul>
          {bcPlanSelectionList.map(
            ({ icon, title, content, tooltip }, index) => (
              <li
                key={index}
                className={classNames(
                  styles.featureItem,
                  !!tooltip && styles.tooltipTarget,
                )}
              >
                {renderTooltip(
                  tooltip,
                  <div className={styles.featureItemWrapper}>
                    {React.cloneElement(icon, {
                      size: 'medium',
                      dangerous_className: styles.featureIcon,
                    })}
                    <div>
                      <span className={styles.featureTitle}>
                        {title} {''}
                        <span className={styles.featureContent}>{content}</span>
                      </span>
                    </div>
                  </div>,
                )}
              </li>
            ),
          )}
        </ul>
      </div>
    </div>
    <div className={styles.actions}>
      <p>{pricePerUser}</p>
      {cta}
      <a href="/business-class" className={styles.learnMore}>
        {format('learn bc')}
      </a>
    </div>
  </>
);
